import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';

import type {
  ThresholdReward_TaskCampaign,
  ThresholdReward_anyRewardConfig,
} from './__generated__/index.graphql.ts';

const LineThrough = styled(BrandS)`
  text-decoration-line: line-through;
  color: var(--c-nd-600);
`;

type Props = {
  originalAnyRewardConfig?: ThresholdReward_anyRewardConfig | null;
  anyRewardConfig?: ThresholdReward_anyRewardConfig | null;
  rewardTaskCampaign?: ThresholdReward_TaskCampaign | null;
  vertical?: boolean;
};
export const ThresholdReward = ({
  originalAnyRewardConfig,
  anyRewardConfig,
  rewardTaskCampaign,
  vertical,
}: Props) => {
  const boostedReward = !!originalAnyRewardConfig && !!rewardTaskCampaign;

  if (!anyRewardConfig) {
    return null;
  }

  if (vertical) {
    return (
      <Vertical gap={0.5} center>
        <RewardConfigImage anyRewardConfig={anyRewardConfig} />
        {boostedReward ? (
          <Horizontal gap={0.5}>
            <BrandS
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': `linear-gradient(323deg, ${rewardTaskCampaign?.color1 || 'var(--c-white)'}, ${rewardTaskCampaign?.color2 || 'var(--c-white)'})`,
                } as CSSProperties
              }
            >
              <RewardConfigText anyRewardConfig={anyRewardConfig} />
            </BrandS>
            <LineThrough>
              <RewardConfigText anyRewardConfig={originalAnyRewardConfig} />
            </LineThrough>
          </Horizontal>
        ) : (
          <BrandS>
            +<RewardConfigText anyRewardConfig={anyRewardConfig} />
          </BrandS>
        )}
      </Vertical>
    );
  }

  return (
    <AnyRewardConfig
      anyRewardConfig={anyRewardConfig}
      variant={Variant.TEXT}
      customText={
        !!boostedReward &&
        (({ rewardText }) => (
          <Horizontal gap={0.5}>
            <BrandS
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': `linear-gradient(323deg, ${rewardTaskCampaign?.color1 || 'var(--c-white)'}, ${rewardTaskCampaign?.color2 || 'var(--c-white)'})`,
                } as CSSProperties
              }
            >
              {rewardText}
            </BrandS>
            <LineThrough>
              <RewardConfigText anyRewardConfig={originalAnyRewardConfig} />
            </LineThrough>
          </Horizontal>
        ))
      }
    />
  );
};

ThresholdReward.fragments = {
  anyRewardConfig: gql`
    fragment ThresholdReward_anyRewardConfig on AnyRewardConfigInterface {
      id
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
      ...AnyRewardConfig_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ThresholdReward_anyRewardConfig>,
  taskCampaign: gql`
    fragment ThresholdReward_TaskCampaign on TaskCampaign {
      slug
      color1: color(variant: PRIMARY)
      color2: color(variant: SECONDARY)
    }
  ` as TypedDocumentNode<ThresholdReward_TaskCampaign>,
};
